import React, { useState } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';

function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  
  // Password change modal state
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [userData, setUserData] = useState(null);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/users/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      });

      const responseText = await response.text();
      let data;
      try {
        data = JSON.parse(responseText);
      } catch {
        throw new Error('Server returned invalid response');
      }

      if (!response.ok) {
        throw new Error(data.detail || 'Invalid email or password');
      }
      
      // Check if user must change password
      if (data.must_change_password) {
        setUserData(data);
        setCurrentPassword(password); // Store current password for the change
        setShowPasswordChange(true);
        setLoading(false);
        return;
      }
      
      // Normal login - save and redirect
      localStorage.setItem('workbook_user_id', data.user_id);
      localStorage.setItem('workbook_user_name', data.name);
      localStorage.setItem('workbook_user_email', email);
      
      window.location.href = '/sketch';
    } catch (error) {
      alert(error.message || 'Invalid email or password. If you just registered, your password is "password" (without quotes).');
      setLoading(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setPasswordError('');
    
    if (newPassword.length < 6) {
      setPasswordError('Password must be at least 6 characters');
      return;
    }
    
    if (newPassword !== confirmNewPassword) {
      setPasswordError('Passwords do not match');
      return;
    }
    
    if (newPassword === 'password') {
      setPasswordError('Please choose a stronger password');
      return;
    }
    
    setLoading(true);
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/users/change-password-required`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: email,
          current_password: currentPassword,
          new_password: newPassword
        })
      });
      
      const responseText = await response.text();
      let data;
      try {
        data = JSON.parse(responseText);
      } catch {
        throw new Error('Server returned invalid response');
      }
      
      if (!response.ok) {
        throw new Error(data.detail || 'Failed to change password');
      }
      
      // Password changed successfully - save user data and redirect
      localStorage.setItem('workbook_user_id', data.user_id);
      localStorage.setItem('workbook_user_name', data.name);
      localStorage.setItem('workbook_user_email', email);
      
      alert('Password changed successfully! Redirecting to Sketch Hub...');
      window.location.href = '/sketch';
      
    } catch (error) {
      setPasswordError(error.message);
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'hsl(138 25% 96%)' }}>
      {/* Password Change Modal */}
      <Dialog open={showPasswordChange} onOpenChange={setShowPasswordChange}>
        <DialogContent className="max-w-md" style={{ borderRadius: 0 }}>
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-center">
              Create Your New Password
            </DialogTitle>
          </DialogHeader>
          <div className="text-center mb-4">
            <p className="text-gray-600">Welcome, <strong>{userData?.name}</strong>!</p>
            <p className="text-sm text-gray-500 mt-1">Please create a secure password for your account.</p>
          </div>
          <form onSubmit={handlePasswordChange} className="space-y-4">
            <div className="space-y-2">
              <Label>New Password *</Label>
              <Input
                type="password"
                required
                minLength={6}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="At least 6 characters"
                className="border-black"
                style={{ borderRadius: 0 }}
                data-testid="new-password-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Confirm New Password *</Label>
              <Input
                type="password"
                required
                value={confirmNewPassword}
                onChange={(e) => setConfirmNewPassword(e.target.value)}
                placeholder="Confirm your new password"
                className="border-black"
                style={{ borderRadius: 0 }}
                data-testid="confirm-new-password-input"
              />
            </div>
            {passwordError && (
              <p className="text-red-600 text-sm text-center">{passwordError}</p>
            )}
            <Button
              type="submit"
              className="w-full bg-black text-white hover:bg-black/90 py-4"
              style={{ borderRadius: 0 }}
              disabled={loading}
              data-testid="change-password-btn"
            >
              {loading ? 'Saving...' : 'Set New Password'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
      
      <Card className="w-full max-w-md" style={{ borderRadius: 0 }}>
        <CardHeader>
          <CardTitle className="text-3xl font-bold text-center">Member Login</CardTitle>
          <p className="text-center text-gray-600 mt-2">The Belonging Sessions</p>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleLogin} className="space-y-4">
            <div className="space-y-2">
              <Label>Email</Label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="border-black"
                style={{ borderRadius: 0 }}
              />
            </div>
            <div className="space-y-2">
              <Label>Password</Label>
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                required
                className="border-black"
                style={{ borderRadius: 0 }}
                data-testid="login-password"
              />
            </div>
            <div className="text-right">
              <a href="/forgot-password" className="text-sm text-blue-600 hover:underline">
                Forgot your password?
              </a>
            </div>
            <Button 
              type="submit" 
              className="w-full bg-black text-white hover:bg-black/90 py-6 text-lg"
              style={{ borderRadius: 0 }}
              disabled={loading}
              data-testid="login-submit-btn"
            >
              {loading ? 'Logging in...' : 'Login'}
            </Button>
            <div className="text-center pt-4 space-y-2">
              <a href="/" className="text-sm text-gray-600 hover:underline block">
                ← Back to Home
              </a>
              <p className="text-xs text-gray-500">
                Don't have an account? <a href="/" className="text-blue-600 hover:underline">Register here</a>
              </p>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

export default Login;
